import { Button, Card, Pagination, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { CampaignDetails, CampaignMemberLog } from "../../models/campaign";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DownloadOutlined } from "@ant-design/icons";
import { useState } from "react";
import ResponsivePagination from "./ResponsivePagination";
import { dashboardService } from "../../services/dashboardService";
import { campaignService } from "../../services/campaignService";

interface CampaignMemberLogTableProps {
  data?: CampaignMemberLog[];
  campaign?: CampaignDetails;
  title?: string;
  runLogId?: number;
}

const CampaignMemberLogTable: React.FC<CampaignMemberLogTableProps> = ({
  data = [],
  campaign,
  title = "Recipient Detail",
  runLogId,
}) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  });

  const isMobileOrTablet = window.innerWidth <= 1024;

  const startIdx = (pagination.current - 1) * pagination.pageSize;
  const endIdx = startIdx + pagination.pageSize;
  const paginatedData = data.slice(startIdx, endIdx);

  const columns: ColumnsType<CampaignMemberLog> = [
    {
      title: "No",
      key: "no",
      render: (_, __, index) =>
        `${(pagination.current - 1) * pagination.pageSize + index + 1}.`,
      width: 70,
    },
    {
      title: "Name",
      dataIndex: "Name",
      key: "name",
    },
    {
      title: "Phone Number",
      key: "phoneNumber",
      render: (_, row) => `${row.ContactIdd ?? ""}${row.ContactNumber ?? ""}`,
    },
    {
      title: "Sent Date & Time",
      dataIndex: "SendDate",
      key: "sendDateTime",
      render: (value: string) =>
        value ? dayjs(value).add(-7, "hour").format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "status",
      render: (status: boolean | number) => {
        const isSuccess = status === true || status === 1;
        return (
          <Tag color={isSuccess ? "green" : "red"}>
            {isSuccess ? "Success" : "Failed"}
          </Tag>
        );
      },
    },
    {
      title: "Notes",
      dataIndex: "Notes",
      key: "notes",
      render: (text?: string) => text || "-",
    },
  ];

  const downloadRecipient = async () => {
    try {
      if (!campaign) {
        console.warn("Campaign is missing");
        return null;
      }
      const blobData = await campaignService.downloadRecipient(
        campaign.Id ?? 0,
        runLogId,
      );

      const blob = new Blob([blobData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = campaign?.Name
        ? campaign.Name.trim().replace(/\s+/g, "_")
        : "Recipient";

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Recipient_${fileName}.xlsx`;
      link.click();

      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Download error:", error);
      alert("An error occurred while generating Excel.");
    }
  };

  return (
    <Card
      title={title}
      className="card-recipient-detail"
      style={{ marginTop: 15 }}
      extra={
        <Button type="primary" onClick={downloadRecipient}>
          <DownloadOutlined />
          Download
        </Button>
      }
    >
      <Table
        size="middle"
        columns={columns}
        dataSource={paginatedData}
        pagination={false}
        rowKey={(row) => row.Id?.toString() ?? Math.random().toString()}
        scroll={{ x: "max-content" }}
      />

      <ResponsivePagination
        totalItems={data.length}
        isMobileOrTablet={isMobileOrTablet}
        onPageChange={(page, pageSize) => {
          setPagination({ current: page, pageSize });
        }}
      />
    </Card>
  );
};

export default CampaignMemberLogTable;
